/**
 * LLM Scan API Service
 *
 * Service functions for interacting with the LLM security scanning API endpoints.
 */

import { apiClient } from "./api";
import type {
  LLMScan,
  LLMScanCreate,
  LLMScanResult,
  LLMScanSummary,
  PaginatedResponse,
  ResultFilters,
  ScanFilters,
} from "@/types/llm";

const API_PREFIX = "/api/v1/redteam";

/**
 * Create a new LLM security scan.
 */
export async function createScan(scanData: LLMScanCreate): Promise<LLMScan> {
  return apiClient.post<LLMScan>(`${API_PREFIX}/scan`, scanData);
}

/**
 * List all LLM scans for the current user with optional filters.
 */
export async function listScans(filters?: ScanFilters): Promise<PaginatedResponse<LLMScan>> {
  const params = new URLSearchParams();
  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.items_per_page) params.append("items_per_page", filters.items_per_page.toString());
  if (filters?.status) params.append("status", filters.status);
  if (filters?.provider_type) params.append("provider_type", filters.provider_type);
  if (filters?.scope) params.append("scope", filters.scope);

  const queryString = params.toString();
  const url = `${API_PREFIX}/scan${queryString ? `?${queryString}` : ""}`;
  return apiClient.get<PaginatedResponse<LLMScan>>(url);
}

/**
 * Get a single LLM scan by UUID.
 */
export async function getScan(scanUuid: string): Promise<LLMScan> {
  return apiClient.get<LLMScan>(`${API_PREFIX}/scan/${scanUuid}`);
}

/**
 * Get aggregated summary statistics for a scan.
 */
export async function getScanSummary(scanUuid: string): Promise<LLMScanSummary> {
  return apiClient.get<LLMScanSummary>(`${API_PREFIX}/scan/${scanUuid}/summary`);
}

/**
 * Get paginated results for a scan with optional filters.
 */
export async function getScanResults(
  scanUuid: string,
  filters?: ResultFilters
): Promise<PaginatedResponse<LLMScanResult>> {
  const params = new URLSearchParams();
  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.items_per_page) params.append("items_per_page", filters.items_per_page.toString());
  if (filters?.status) params.append("status", filters.status);
  if (filters?.severity) params.append("severity", filters.severity);
  if (filters?.plugin_id) params.append("plugin_id", filters.plugin_id);

  const queryString = params.toString();
  const url = `${API_PREFIX}/scan/${scanUuid}/results${queryString ? `?${queryString}` : ""}`;
  return apiClient.get<PaginatedResponse<LLMScanResult>>(url);
}

/**
 * Get a single scan result by UUID.
 */
export async function getScanResult(resultUuid: string): Promise<LLMScanResult> {
  return apiClient.get<LLMScanResult>(`${API_PREFIX}/scan-result/${resultUuid}`);
}

/**
 * Update scan visibility (make it visible to organization).
 */
export async function updateScanVisibility(
  scanUuid: string,
  isOrgVisible: boolean
): Promise<LLMScan> {
  return apiClient.patch<LLMScan>(
    `${API_PREFIX}/scan/${scanUuid}?is_org_visible=${isOrgVisible}`
  );
}
